import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../../store';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import LeftSide from '../../main/left-side';
import RightSide from '../../main/right-side';
import TitleCard from '../../main/title-card';
import useTranslate from '../../useTranslate';
import { LoadLocation, LocationDto } from '../../../store/locationsSlice';
import SuggestedCostIncreaseCard from './suggested-cost-increase-card';
import { LoadMansions, Mansion } from '../../../store/mansionsSlice';
import { isFinal } from '../../../store/network/managedRequest';
import Spinner from '../../util/spinner';
import {
    LoadLocationCosts,
    LocationCostsDto,
} from '../../../store/locationCostsSlice';
import { PbRequest } from '../../../store/pbRequest';
import { PbGenericRequest } from '../../../store/pbRequestSlice';

interface Props {
    getLocationRequest: PbGenericRequest;
    getLocationData: LocationDto | undefined;
    loadMansions: PbGenericRequest;
    loadMansionsData: Mansion[];
    locationCostsRequest: PbGenericRequest;
    locationCostsData: LocationCostsDto | undefined;
    LoadLocation: (locationId: number) => void;
    LoadMansions: () => void;
    LoadLocationCosts: (locationId: number) => void;
}

function LocationSuggestedPricesScreen(props: Readonly<Props>) {
    const strings = useTranslate('LocationPricing');
    const navigate = useNavigate();

    const params = useParams();
    const mansionId = Number(params.object_id);
    const locationId = Number(params.location_id);

    const pricingScreenUrl = `/objects/${mansionId}/${locationId}/pricing`;

    useEffect(() => {
        props.LoadLocation(locationId);
        props.LoadMansions();
        props.LoadLocationCosts(locationId);
    }, [locationId]);

    const location: LocationDto | undefined = props.getLocationData;
    const shouldShowSuggestion = !!location?.cost_increase_suggestion;

    useEffect(() => {
        if (location?.id === locationId && !shouldShowSuggestion) {
            navigate(pricingScreenUrl, { replace: true });
        }
    }, [
        location,
        locationId,
        shouldShowSuggestion,
        navigate,
        pricingScreenUrl,
    ]);

    if (
        !isFinal(props.loadMansions.status) ||
        !isFinal(props.getLocationRequest.status) ||
        !isFinal(props.locationCostsRequest.status)
    ) {
        return <Spinner />;
    }

    const mansions: Mansion[] = props.loadMansionsData;
    const mansion = mansions.find((mansion) => mansion.id === mansionId);
    const locationCosts: LocationCostsDto = props.locationCostsData;

    return (
        <Row>
            <LeftSide addClass="">
                <TitleCard
                    title={strings.title}
                    left={{
                        content: (
                            <Link
                                to={`/dashboard`}
                                className="icon icon-wew-arrow-left icon-sm"
                            />
                        ),
                        title: strings.cancel,
                    }}
                />
            </LeftSide>

            <Col xs={6}>
                {shouldShowSuggestion && locationCosts && (
                    <Row>
                        <SuggestedCostIncreaseCard
                            suggestion={location.cost_increase_suggestion}
                            locationCosts={locationCosts}
                            goToPricingScreen={() =>
                                navigate(pricingScreenUrl, { replace: false })
                            }
                        />
                    </Row>
                )}
            </Col>

            <RightSide>
                <div className={'card level-1'}>
                    <div className="card-content">
                        {mansion?.address && (
                            <div>
                                <div>{location?.name}</div>
                                <div>
                                    {mansion.address.street}{' '}
                                    {mansion.address.housenumber}
                                </div>
                                <div>
                                    {mansion.address.postalcode}{' '}
                                    {mansion.address.city}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </RightSide>
        </Row>
    );
}

export default connect(
    (store: RootState) => ({
        getLocationRequest: store.pbRequest[PbRequest.GET_LOCATION],
        getLocationData: store.locations.location,
        loadMansions: store.pbRequest[PbRequest.LOAD_MANSIONS],
        loadMansionsData: store.mansions.mansions,
        locationCostsRequest: store.pbRequest[PbRequest.GET_LOCATION_COSTS],
        locationCostsData: store.locationCosts.locationCosts,
    }),
    {
        LoadLocation,
        LoadMansions,
        LoadLocationCosts,
    }
)(LocationSuggestedPricesScreen);
